const API_BASE = import.meta.env.VITE_APP_API_URL || "/api";

const TOKEN_KEY = "excalidraw-auth-token";

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  if (options.body) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return res.json();
}

// --- Types ---

export interface ApiUser {
  id: string;
  email: string;
  name: string | null;
  avatar?: string | null;
  is_admin?: boolean;
}

export interface TagInfo {
  id: string;
  name: string;
  color: string;
}

export interface FolderInfo {
  id: string;
  parent_id: string | null;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface DrawingMetadata {
  id: string;
  name: string;
  thumbnail: string | null;
  folder_id: string | null;
  tags: TagInfo[];
  created_at: string;
  updated_at: string;
}

export interface DrawingFull extends DrawingMetadata {
  elements: any[];
  app_state: Record<string, any>;
  appState: Record<string, any>;
  files: Record<string, any>;
}

// --- Auth ---

export async function apiRegister(
  email: string,
  password: string,
  name?: string,
): Promise<{ token: string; user: ApiUser }> {
  const result = await request<{ token: string; user: ApiUser }>(
    "/auth/register",
    {
      method: "POST",
      body: JSON.stringify({ email, password, name }),
    },
  );
  setToken(result.token);
  return result;
}

export async function apiLogin(
  email: string,
  password: string,
): Promise<{ token: string; user: ApiUser }> {
  const result = await request<{ token: string; user: ApiUser }>(
    "/auth/login",
    {
      method: "POST",
      body: JSON.stringify({ email, password }),
    },
  );
  setToken(result.token);
  return result;
}

export async function apiGetMe(): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>("/auth/me");
  return result.user;
}

export async function apiUpdateMe(data: {
  name?: string | null;
  avatar?: string | null;
}): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>("/auth/me", {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return result.user;
}

export async function apiChangePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await request<{ ok: true }>("/auth/me/password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function apiDeleteMe(password: string): Promise<void> {
  await request<{ ok: true }>("/auth/me", {
    method: "DELETE",
    body: JSON.stringify({ password }),
  });
  setToken(null);
}

export function apiLogout() {
  setToken(null);
}

export function isLoggedIn(): boolean {
  return !!getToken();
}

// --- Library ---

export interface ApiLibraryItem {
  id: string;
  name: string | null;
  status: "published" | "unpublished";
  elements: any[];
  created: number;
}

export async function apiGetLibrary(): Promise<ApiLibraryItem[]> {
  const result = await request<{ libraryItems: ApiLibraryItem[] }>("/library");
  return result.libraryItems;
}

export async function apiPutLibrary(
  libraryItems: ApiLibraryItem[],
): Promise<void> {
  await request("/library", {
    method: "PUT",
    body: JSON.stringify({ libraryItems }),
  });
}

// --- Drawings ---

export async function apiListDrawings(): Promise<DrawingMetadata[]> {
  const result = await request<{ drawings: DrawingMetadata[] }>("/drawings");
  return result.drawings;
}

export async function apiGetDrawing(id: string): Promise<DrawingFull> {
  const result = await request<{ drawing: DrawingFull }>(`/drawings/${id}`);
  return result.drawing;
}

export async function apiCreateDrawing(data: {
  name?: string;
  elements?: any[];
  appState?: Record<string, any>;
  thumbnail?: string;
  folderId?: string | null;
}): Promise<DrawingMetadata> {
  const result = await request<{ drawing: DrawingMetadata }>("/drawings", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return result.drawing;
}

export async function apiUpdateDrawing(
  id: string,
  data: {
    name?: string;
    elements?: any[];
    appState?: Record<string, any>;
    thumbnail?: string;
    folderId?: string | null;
  },
): Promise<DrawingMetadata> {
  const result = await request<{ drawing: DrawingMetadata }>(
    `/drawings/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
  return result.drawing;
}

// Fire-and-forget save that survives page unload (keepalive fetch)
// Browsers limit keepalive request bodies to 64KB, so we strip unnecessary data.
export function apiUpdateDrawingKeepAlive(
  id: string,
  data: { elements?: any[]; appState?: Record<string, any> },
): void {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Strip deleted elements and volatile appState to stay under 64KB keepalive limit
  const minimalData: Record<string, any> = {};
  if (data.elements) {
    minimalData.elements = data.elements.filter((el: any) => !el.isDeleted);
  }
  if (data.appState) {
    const {
      cursorButton,
      scrollX,
      scrollY,
      zoom,
      selectedElementIds,
      selectedGroupIds,
      editingGroupId,
      editingElement,
      ...persistableState
    } = data.appState;
    minimalData.appState = persistableState;
  }

  const body = JSON.stringify(minimalData);

  // If payload still exceeds ~60KB, skip keepalive and rely on the last regular sync
  if (body.length > 60000) {
    console.warn(
      "Cloud sync payload too large for keepalive (%d bytes), skipping unload save",
      body.length,
    );
    return;
  }

  fetch(`${API_BASE}/drawings/${id}`, {
    method: "PUT",
    headers,
    body,
    keepalive: true,
  }).catch(() => {}); // fire-and-forget
}

export async function apiDeleteDrawing(id: string): Promise<void> {
  await request(`/drawings/${id}`, { method: "DELETE" });
}

export async function apiDuplicateDrawing(
  id: string,
): Promise<DrawingMetadata> {
  const result = await request<{ drawing: DrawingMetadata }>(
    `/drawings/${id}/duplicate`,
    { method: "POST" },
  );
  return result.drawing;
}

// --- Admin ---

export async function apiAdminListUsers(): Promise<ApiUser[]> {
  const result = await request<{ users: ApiUser[] }>("/admin/users");
  return result.users;
}

export async function apiAdminCreateUser(data: {
  email: string;
  password: string;
  name?: string;
  is_admin?: boolean;
}): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>("/auth/register", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return result.user;
}

export async function apiAdminUpdateUser(
  id: string,
  data: {
    name?: string;
    email?: string;
    is_admin?: boolean;
    password?: string;
  },
): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>(`/admin/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return result.user;
}

export async function apiAdminDeleteUser(id: string): Promise<void> {
  await request(`/admin/users/${id}`, { method: "DELETE" });
}

// --- Comments ---

export interface ElementComment {
  id: string;
  element_id: string;
  text: string;
  author_name: string | null;
  author_email: string;
  created_at: string;
  updated_at: string;
}

export async function apiListComments(
  drawingId: string,
): Promise<{ comments: ElementComment[]; canEdit: boolean }> {
  const result = await request<{
    comments: ElementComment[];
    canEdit: boolean;
  }>(`/drawings/${drawingId}/comments`);
  return { comments: result.comments, canEdit: result.canEdit };
}

export async function apiCreateComment(
  drawingId: string,
  elementId: string,
  text: string,
): Promise<ElementComment> {
  const result = await request<{ comment: ElementComment }>(
    `/drawings/${drawingId}/comments`,
    {
      method: "POST",
      body: JSON.stringify({ elementId, text }),
    },
  );
  return result.comment;
}

export async function apiUpdateComment(
  drawingId: string,
  commentId: string,
  text: string,
): Promise<ElementComment> {
  const result = await request<{ comment: ElementComment }>(
    `/drawings/${drawingId}/comments/${commentId}`,
    {
      method: "PUT",
      body: JSON.stringify({ text }),
    },
  );
  return result.comment;
}

export async function apiDeleteComment(
  drawingId: string,
  commentId: string,
): Promise<void> {
  await request(`/drawings/${drawingId}/comments/${commentId}`, {
    method: "DELETE",
  });
}

// --- Folders ---

export async function apiListFolders(): Promise<FolderInfo[]> {
  const result = await request<{ folders: FolderInfo[] }>("/folders");
  return result.folders;
}

export async function apiCreateFolder(data: {
  name?: string;
  parentId?: string | null;
}): Promise<FolderInfo> {
  const result = await request<{ folder: FolderInfo }>("/folders", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return result.folder;
}

export async function apiUpdateFolder(
  id: string,
  data: { name?: string; parentId?: string | null },
): Promise<FolderInfo> {
  const result = await request<{ folder: FolderInfo }>(`/folders/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return result.folder;
}

export async function apiDeleteFolder(id: string): Promise<void> {
  await request(`/folders/${id}`, { method: "DELETE" });
}

// --- Tags ---

export async function apiListTags(): Promise<TagInfo[]> {
  const result = await request<{ tags: TagInfo[] }>("/tags");
  return result.tags;
}

export async function apiCreateTag(data: {
  name: string;
  color?: string;
}): Promise<TagInfo> {
  const result = await request<{ tag: TagInfo }>("/tags", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return result.tag;
}

export async function apiUpdateTag(
  id: string,
  data: { name?: string; color?: string },
): Promise<TagInfo> {
  const result = await request<{ tag: TagInfo }>(`/tags/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return result.tag;
}

export async function apiDeleteTag(id: string): Promise<void> {
  await request(`/tags/${id}`, { method: "DELETE" });
}

export async function apiSetDrawingTags(
  drawingId: string,
  tagIds: string[],
): Promise<TagInfo[]> {
  const result = await request<{ tags: TagInfo[] }>(
    `/drawings/${drawingId}/tags`,
    {
      method: "PUT",
      body: JSON.stringify({ tagIds }),
    },
  );
  return result.tags;
}

// --- Shares (view-only links) ---

export interface ShareInfo {
  id: string;
  share_token: string;
  created_at: string;
}

export interface SharedDrawing {
  name: string;
  elements: any[];
  appState: Record<string, any>;
  authorName: string;
  files: Record<string, any>;
}

export async function apiCreateShareLink(
  drawingId: string,
): Promise<ShareInfo> {
  const result = await request<{ share: ShareInfo }>(
    `/drawings/${drawingId}/share`,
    { method: "POST" },
  );
  return result.share;
}

export async function apiRevokeShareLink(drawingId: string): Promise<void> {
  await request(`/drawings/${drawingId}/share`, { method: "DELETE" });
}

export async function apiGetSharedDrawing(
  token: string,
): Promise<SharedDrawing> {
  const res = await fetch(`${API_BASE}/shared/${token}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.drawing;
}

// --- Files ---

export async function apiSaveFiles(
  drawingId: string,
  files: Array<{ id: string; mimeType: string; dataURL: string }>,
): Promise<void> {
  await request(`/drawings/${drawingId}/files`, {
    method: "PUT",
    body: JSON.stringify({ files }),
  });
}

// --- Compartilhamento com usuários internos (ACL) ---

export type Permission = "view" | "edit" | "collab";

export interface SearchedUser {
  id: string;
  email: string;
  name: string | null;
}

export interface Collaborator {
  user_id: string;
  email: string;
  name: string | null;
  permission: Permission;
  message: string | null;
  created_at: string;
  granted_by_email: string;
  granted_by_name: string | null;
}

export async function apiSearchUsers(q: string): Promise<SearchedUser[]> {
  const trimmed = q.trim();
  if (!trimmed) return [];
  const result = await request<{ users: SearchedUser[] }>(
    `/users/search?q=${encodeURIComponent(trimmed)}`,
  );
  return result.users;
}

export async function apiListAllUsers(): Promise<SearchedUser[]> {
  const result = await request<{ users: SearchedUser[] }>("/users");
  return result.users;
}

export async function apiAddCollaborators(
  drawingId: string,
  payload: {
    userIds: string[];
    permission: Permission;
    message?: string;
    roomUrl?: string;
  },
): Promise<void> {
  await request(`/drawings/${drawingId}/collaborators`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function apiListCollaborators(
  drawingId: string,
): Promise<Collaborator[]> {
  const result = await request<{ collaborators: Collaborator[] }>(
    `/drawings/${drawingId}/collaborators`,
  );
  return result.collaborators;
}

export async function apiUpdateCollaboratorPermission(
  drawingId: string,
  userId: string,
  permission: Permission,
): Promise<void> {
  await request(`/drawings/${drawingId}/collaborators/${userId}`, {
    method: "PATCH",
    body: JSON.stringify({ permission }),
  });
}

export async function apiRemoveCollaborator(
  drawingId: string,
  userId: string,
): Promise<void> {
  await request(`/drawings/${drawingId}/collaborators/${userId}`, {
    method: "DELETE",
  });
}

// --- "Compartilhado comigo" e fork ---

export interface SharedWithMeDrawing {
  id: string;
  name: string;
  thumbnail: string | null;
  created_at: string;
  updated_at: string;
  permission: Permission;
  message: string | null;
  shared_at: string;
  owner_id: string;
  owner_name: string | null;
  owner_email: string;
}

export async function apiListSharedWithMe(): Promise<SharedWithMeDrawing[]> {
  const result = await request<{ drawings: SharedWithMeDrawing[] }>(
    "/drawings/shared-with-me",
  );
  return result.drawings;
}

export async function apiForkDrawing(
  drawingId: string,
): Promise<DrawingMetadata> {
  const result = await request<{ drawing: DrawingMetadata }>(
    `/drawings/${drawingId}/fork`,
    { method: "POST" },
  );
  return result.drawing;
}

// --- Compartilhamento de PASTAS (ACL com herança) ---
// Compartilhar uma pasta concede acesso a todo o conteúdo dela e das
// subpastas, atuais e futuros. Só há dois níveis: 'view' e 'edit'.

export type FolderPermission = "view" | "edit";

export interface FolderCollaborator {
  user_id: string;
  email: string;
  name: string | null;
  permission: FolderPermission;
  message: string | null;
  created_at: string;
  granted_by_email: string;
  granted_by_name: string | null;
}

export async function apiAddFolderCollaborators(
  folderId: string,
  payload: {
    userIds: string[];
    permission: FolderPermission;
    message?: string;
  },
): Promise<void> {
  await request(`/folders/${folderId}/collaborators`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function apiListFolderCollaborators(
  folderId: string,
): Promise<FolderCollaborator[]> {
  const result = await request<{ collaborators: FolderCollaborator[] }>(
    `/folders/${folderId}/collaborators`,
  );
  return result.collaborators;
}

export async function apiUpdateFolderCollaboratorPermission(
  folderId: string,
  userId: string,
  permission: FolderPermission,
): Promise<void> {
  await request(`/folders/${folderId}/collaborators/${userId}`, {
    method: "PATCH",
    body: JSON.stringify({ permission }),
  });
}

export async function apiRemoveFolderCollaborator(
  folderId: string,
  userId: string,
): Promise<void> {
  await request(`/folders/${folderId}/collaborators/${userId}`, {
    method: "DELETE",
  });
}

export interface SharedWithMeFolder {
  id: string;
  parent_id: string | null;
  name: string;
  created_at: string;
  updated_at: string;
  permission: FolderPermission;
  message: string | null;
  shared_at: string;
  owner_id: string;
  owner_name: string | null;
  owner_email: string;
}

export async function apiListSharedFoldersWithMe(): Promise<
  SharedWithMeFolder[]
> {
  const result = await request<{ folders: SharedWithMeFolder[] }>(
    "/folders/shared-with-me",
  );
  return result.folders;
}

export interface FolderContents {
  folder: FolderInfo;
  subfolders: FolderInfo[];
  drawings: DrawingMetadata[];
  access_role: "owner" | FolderPermission;
}

export async function apiGetFolderContents(
  folderId: string,
): Promise<FolderContents> {
  return request<FolderContents>(`/folders/${folderId}/contents`);
}

// Chamado pelo destinatário ao aceitar um convite de colaboração: cria uma
// notificação de pedido no inbox do dono do drawing. Bob então fica
// esperando a resposta (polling em apiListNotifications).
export async function apiRequestCollabJoin(drawingId: string): Promise<void> {
  await request(`/drawings/${drawingId}/collab/request-join`, {
    method: "POST",
  });
}

// Chamado pelo dono do drawing ao aceitar ou recusar o pedido. Em caso de
// aceite, ele já iniciou a sala localmente e envia o roomUrl. O destinatário
// é notificado e seu polling redireciona-o pra sala.
export async function apiRespondCollabJoin(
  drawingId: string,
  payload: { recipientUserId: string; roomUrl?: string; decline?: boolean },
): Promise<void> {
  await request(`/drawings/${drawingId}/collab/respond-join`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Chamado pelo dono ao parar a sessão de colab. Server só dispara notif se
// quem chamou for de fato o dono (no-op silencioso caso contrário). Frontend
// sempre chama; servidor decide.
export async function apiEndCollabSession(
  drawingId: string,
  roomUrl: string,
): Promise<void> {
  await request(`/drawings/${drawingId}/collab/end-session`, {
    method: "POST",
    body: JSON.stringify({ roomUrl }),
  });
}

// --- Notificações ---

export type NotificationType =
  | "drawing_shared"
  | "folder_shared"
  | "collab_join_request"
  | "collab_join_accepted"
  | "collab_join_declined"
  | "collab_session_ended";

export interface NotificationItem {
  id: string;
  type: NotificationType;
  payload: {
    // presentes em notificações de drawing (drawing_shared / collab_*)
    drawingId: string;
    drawingName: string;
    // presentes em folder_shared
    folderId?: string;
    folderName?: string;
    fromUserId: string;
    fromName: string;
    // presente em drawing_shared / folder_shared
    permission?: Permission;
    message?: string | null;
    // presente em collab_join_accepted
    roomUrl?: string | null;
  };
  read_at: string | null;
  created_at: string;
}

export async function apiListNotifications(): Promise<NotificationItem[]> {
  const result = await request<{ notifications: NotificationItem[] }>(
    "/notifications",
  );
  return result.notifications;
}

export async function apiNotificationsUnreadCount(): Promise<number> {
  const result = await request<{ count: number }>(
    "/notifications/unread-count",
  );
  return result.count;
}

export async function apiMarkNotificationRead(id: string): Promise<void> {
  await request(`/notifications/${id}/read`, { method: "PATCH" });
}

export async function apiMarkAllNotificationsRead(): Promise<void> {
  await request("/notifications/mark-all-read", { method: "POST" });
}

export async function apiDeleteNotification(id: string): Promise<void> {
  await request(`/notifications/${id}`, { method: "DELETE" });
}
